import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Container from 'common/components/Container';

import * as ActiveDiscountSelectors from 'selectors/ActiveDiscountSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveDiscountActions from 'actions/ActiveDiscountActions';
import useDebouncedForm from 'common/hooks/useDebouncedForm';
import { DiscountCodeName } from '../components/DiscountCodeName';
import DiscountType from '../components/DiscountType';
import DiscountUsageLimit from '../components/DiscountUsageLimit';
import { DiscountFormErrors } from '.';
import DiscountLimits from '../components/DiscountLimits';
import { DiscountCodeValidity } from '../types';
import DiscountValidityLimit from '../components/DiscountValidityLimit';

interface Props {
	formErrors: DiscountFormErrors;
}

const EditDiscount = (props: Props) => {
	const dispatch = useDispatch();
	const activeDiscount = useSelector(ActiveDiscountSelectors.discountCodeData)!;
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(() => activeDiscount, [activeDiscount]),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveDiscountActions.editLocalDiscount(changes));
			},
			[dispatch],
		),
	});

	const validity: DiscountCodeValidity = {
		validFrom: state.validFrom,
		validTo: state.validTo,
		bookingStartDateFrom: state.bookingStartDateFrom,
		bookingStartDateTo: state.bookingStartDateTo,
	};

	return (
		<Container densePaddingTop>
			<DiscountCodeName
				value={state.code}
				onChange={(value) => editState({ code: value })}
				error={props.formErrors.name}
			/>
			<DiscountType
				priceRule={state.priceRule}
				onChange={(priceRule) => editState({ priceRule })}
				error={props.formErrors.value}
				currency={shopCurrency}
			/>
			<DiscountLimits
				entitledCategoryIds={state.entitledCategoryIds}
				entitledProductIds={state.entitledProductIds}
				onCategoryChange={(entitledCategoryIds) => editState({ entitledCategoryIds })}
				onProductChange={(entitledProductIds) => editState({ entitledProductIds })}
			/>
			<DiscountUsageLimit
				limit={state.usageLimit ?? null}
				onLimitChange={(limit) => editState({ usageLimit: limit })}
			/>
			<DiscountValidityLimit
				validity={validity}
				onValidityChange={(validity) => editState({ ...validity })}
			/>
		</Container>
	);
};

export default EditDiscount;
